import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from "recharts";
import { Lightbulb } from "lucide-react";

const COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function Stat({ label, value }) {
  return (
    <Card className="p-6 rounded-2xl border-border shadow-none">
      <div className="overline mb-2">{label}</div>
      <div className="font-serif text-4xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data));
    api.get("/analytics/insights").then((r) => setInsights(r.data.insights || [])).catch(() => {});
  }, []);

  if (!data) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-muted-foreground" data-testid="analytics-loading">Loading analytics…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="analytics-page">
      <div className="overline mb-3">Food Intelligence Analytics</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight">What the world is craving</h1>

      {/* Intelligence Insights */}
      <Card className="mt-10 p-7 rounded-2xl border-border shadow-none bg-primary/[0.04]" data-testid="analytics-insights-card">
        <div className="flex items-center gap-2 mb-1">
          <Lightbulb className="w-4 h-4 text-primary" strokeWidth={1.75} />
          <div className="overline text-primary">YUMO Intelligence</div>
        </div>
        <h3 className="font-serif text-2xl font-semibold mb-4">Insights</h3>
        {insights.length === 0 ? (
          <div className="text-sm text-muted-foreground">Use Recommend a few times to unlock personal insights.</div>
        ) : (
          <ul className="space-y-2 text-sm md:text-base">
            {insights.map((s, i) => (
              <li key={i} className="flex gap-2"><span className="text-primary">→</span><span>{s}</span></li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10">
        <Stat label="Total Users" value={data.totals.users} />
        <Stat label="Dishes in Universe" value={data.totals.foods} />
        <Stat label="Recommendations" value={data.totals.recommendations} />
        <Stat label="Feedback Events" value={data.totals.feedback} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-10">
        <Card className="lg:col-span-7 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Most Recommended Foods</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">Top picks from Mother Drive</h3>
          {data.most_recommended.length === 0 ? (
            <div className="text-muted-foreground text-sm h-56 flex items-center justify-center" data-testid="most-rec-empty">
              No recommendations yet. Try the Recommend page.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.most_recommended} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="lg:col-span-5 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Mood Trends</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">Why people eat</h3>
          {data.mood_trends.length === 0 ? (
            <div className="text-muted-foreground text-sm h-56 flex items-center justify-center">No mood data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.mood_trends} dataKey="count" nameKey="mood" innerRadius={50} outerRadius={100} paddingAngle={3}>
                  {data.mood_trends.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="lg:col-span-5 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Weather Trends</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">Context behind cravings</h3>
          {data.weather_trends.length === 0 ? (
            <div className="text-muted-foreground text-sm h-56 flex items-center justify-center">No weather data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.weather_trends}>
                <XAxis dataKey="weather" tick={{ fontSize: 12 }} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="lg:col-span-7 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Cuisine Preferences</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">Where the universe leans</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.cuisine_breakdown.slice(0, 10)}>
              <XAxis dataKey="cuisine" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} />
              <YAxis hide />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Bar dataKey="count" fill="hsl(var(--foreground))" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="lg:col-span-6 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Diet Preferences</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">User dietary split</h3>
          {data.diet_preferences.length === 0 ? (
            <div className="text-muted-foreground text-sm h-40 flex items-center justify-center" data-testid="diet-empty">No diet data — users haven't set profiles yet.</div>
          ) : (
            <div className="space-y-3">
              {data.diet_preferences.map((d, i) => (
                <div key={d.diet} className="flex items-center gap-3">
                  <div className="overline w-32 capitalize">{d.diet}</div>
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, d.count * 20)}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                  <div className="font-medium tabular-nums w-8 text-right">{d.count}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-6 p-7 rounded-2xl border-border shadow-none">
          <div className="overline mb-1">Most Accepted Foods</div>
          <h3 className="font-serif text-2xl font-semibold mb-5">What users actually loved</h3>
          {data.most_accepted.length === 0 ? (
            <div className="text-muted-foreground text-sm h-40 flex items-center justify-center" data-testid="accepted-empty">
              No "I'll eat this" feedback yet.
            </div>
          ) : (
            <div className="space-y-3">
              {data.most_accepted.map((f, i) => (
                <div key={f.name} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-muted-foreground">{f.cuisine}</div>
                  </div>
                  <div className="font-serif text-xl font-semibold tabular-nums">{f.count}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
